import { QueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useErrorStore } from "../store/errorStore";
import { logger } from "../utils/logger";
import { BaseError } from "viem";

const isUserRejected = (msg: string) =>
  /user rejected|user denied|denied transaction signature|rejected the request|ACTION_REJECTED/i.test(
    msg
  );

export const getErrorMessage = (error: unknown): string | null => {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Network error: Unable to connect to the server.";
    }
    if (error.response.status >= 500) {
      return "Server error: Please try again later.";
    }

    return null;
  }

  if (error instanceof BaseError) {
    const msg = error.shortMessage || error.message || "";

    if (isUserRejected(msg)) return null;

    return msg || "Transaction failed.";
  }

  return "Unknown error occurred.";
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error) && !error.response) {
          return failureCount < 3;
        }
        return false;
      },
    },
    mutations: {
      onError: (error) => {
        const { setErrorMessage } = useErrorStore.getState();
        const errorMessage = getErrorMessage(error);

        setErrorMessage(errorMessage);
        logger.error("[React Query] Mutation error:", {
          message: errorMessage,
          fullError: error,
        });
      },
    },
  },
});

queryClient.getQueryCache().subscribe((event) => {
  if (event?.type === "observerResultsUpdated") {
    const { error } = event.query.state;
    if (error) {
      const { setErrorMessage } = useErrorStore.getState();
      const errorMessage = getErrorMessage(error);

      setErrorMessage(errorMessage);
      logger.error("[React Query] Query error:", {
        message: errorMessage,
        fullError: error,
        queryKey: event.query.queryKey,
      });
    }
  }
});
